import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, ArrowRight, Camera, Check, ChevronLeft, Pencil, X } from "lucide-react";
import { Navbar } from "./Navbar";
import { Bild } from "./Bild";
import { START, STRECKE, TEXTE as T } from "../lib/inhalte";

// Offertanfrage (Neugestaltung 2026-10-22, Vorbild: Klickstrecke mit einer Frage pro Bildschirm).
// Keine Preise, keine Antwortfrist, keine Registrierung. Die Auswahl beschreibt Wünsche, keine Zusagen.
const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/offertanfrage`;
const FOTOS_MAX = 6;
const ZUSAMMENFASSUNG = STRECKE.length;
const GESAMT = STRECKE.length + 1;

const LEER = {
  boden: "", lage: "", nutzung: "", gestaltung: "", oberflaeche_wahl: "", flaeche: "",
  plz: "", ort: "", email: "", telefon: "", name: "", info: "", website: "", leistung: "",
};
const LEISTUNGEN = Object.fromEntries(START.leistungen.items.map((l) => [l.id, l.titel]));
// Über eine Leistungskarte gekommen (/offerte?leistung=flakes): Leistung merken und passende Antwort vorauswählen
const VORWAHL = { flakes: { gestaltung: "flakes" }, versiegelung: { gestaltung: "versiegelung" }, rutsch: { oberflaeche_wahl: "rutschhemmend" } };
const anfang = (suche) => {
  const l = new URLSearchParams(suche).get("leistung");
  return LEISTUNGEN[l] ? { ...LEER, leistung: l, ...VORWAHL[l] } : LEER;
};
const option = (id, wert) => STRECKE.find((x) => x.id === id).optionen.find((o) => o.wert === wert);
const nr = (id) => STRECKE.findIndex((x) => x.id === id);

const EMAIL_OK = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(e.trim());

// Foto vor dem Senden verkleinern (längste Seite 1600 px, JPEG). Geht das nicht (z. B. HEIC), bleibt das Original.
async function verkleinern(datei) {
  try {
    const bild = await createImageBitmap(datei);
    const f = Math.min(1, 1600 / Math.max(bild.width, bild.height));
    const c = document.createElement("canvas");
    c.width = Math.round(bild.width * f);
    c.height = Math.round(bild.height * f);
    c.getContext("2d").drawImage(bild, 0, 0, c.width, c.height);
    const blob = await new Promise((ok) => c.toBlob(ok, "image/jpeg", 0.82));
    return blob || datei;
  } catch {
    return datei;
  }
}

// Eine Antwortkarte: Bild oben, Text unten. „?“-Karten gestrichelt, Flächenkarten mit grosser Zahl.
const Karte = ({ o, aktiv, onClick, testid, nr }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    aria-pressed={aktiv}
    aria-label={o.text}
    style={{ animationDelay: `${nr * 55}ms` }}
    className={`aufpoppen group relative text-left rounded-lg overflow-hidden transition-all duration-150 bg-white ${
      o.fragezeichen ? "border-2 border-dashed" : "border-2"
    } ${aktiv ? "border-chemara-violet shadow-md" : "border-stein-200 hover:border-stein-400 hover:shadow-sm"}`}
  >
    {o.bild || (o.fragezeichen && !o.gross) ? (
      <Bild id={o.bild || "fragezeichen"} className="aspect-[4/3] w-full" />
    ) : (
      <div className="aspect-[4/3] w-full grid place-items-center bg-stein-100">
        <span className="font-heading text-3xl sm:text-4xl text-stein-700">{o.gross}</span>
      </div>
    )}
    <div className="px-3 py-3 sm:px-4 text-[15px] sm:text-base font-medium text-stein-900 leading-snug min-h-[3.25rem] flex items-center">
      {o.text}
    </div>
    {aktiv && (
      <span className="absolute top-2 right-2 h-7 w-7 rounded-full grid place-items-center bg-chemara-violet text-white">
        <Check size={16} />
      </span>
    )}
  </button>
);

const Feld = ({ label, children }) => (
  <label className="block">
    <span className="block text-sm text-stein-700 mb-1.5 font-medium">{label}</span>
    {children}
  </label>
);
const EINGABE =
  "w-full bg-white border border-stein-300 rounded-md focus:border-chemara-violet focus:ring-2 focus:ring-chemara-violet/20 outline-none px-4 py-3.5 text-base text-stein-900 placeholder:text-stein-400";
const KNOPF =
  "inline-flex items-center justify-center gap-2 px-6 py-4 rounded-md bg-chemara-violet text-white font-semibold hover:bg-chemara-violet-deep disabled:opacity-40 disabled:hover:bg-chemara-violet transition-colors";

export const Offertanfrage = () => {
  const { search } = useLocation();
  const [a, setA] = useState(() => anfang(search));
  const [fotos, setFotos] = useState([]);              // [{blob, url}]
  const [i, setI] = useState(0);
  const [ausZusammenfassung, setAusZusammenfassung] = useState(false);
  const [status, setStatus] = useState("");            // "", "sendet", "ok", "test"
  const [fehler, setFehler] = useState("");
  const dateiwahl = useRef(null);
  const setze = (x) => setA((v) => ({ ...v, ...x }));

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [i, status]);

  const schritt = STRECKE[i];
  const weiter = () => {
    setFehler("");
    if (ausZusammenfassung) {
      setAusZusammenfassung(false);
      setI(ZUSAMMENFASSUNG);
    } else setI((n) => Math.min(n + 1, ZUSAMMENFASSUNG));
  };
  const zurueck = () => {
    setFehler("");
    if (ausZusammenfassung) {
      setAusZusammenfassung(false);
      setI(ZUSAMMENFASSUNG);
    } else setI((n) => Math.max(n - 1, 0));
  };
  const aendern = (n) => {
    setAusZusammenfassung(true);
    setI(n);
  };
  const waehlen = (feld, wert) => {
    setze({ [feld]: wert });
    setTimeout(weiter, 260);                           // antippen = weiter
  };

  const fertig = {
    standort: /^\d{4}$/.test(a.plz),
    kontakt: EMAIL_OK(a.email),
  };

  const fotosHinzu = async (liste) => {
    const neu = [];
    for (const d of Array.from(liste).slice(0, FOTOS_MAX - fotos.length)) {
      if (!d.type.startsWith("image/") && !/\.hei[cf]$/i.test(d.name)) continue;
      const blob = await verkleinern(d);
      neu.push({ blob, url: URL.createObjectURL(blob) });
    }
    setFotos((v) => [...v, ...neu]);
  };

  const senden = async () => {
    setStatus("sendet");
    setFehler("");
    const o = option("oberflaeche_wahl", a.oberflaeche_wahl);
    const daten = {
      boden: a.boden, lage: a.lage, nutzung: [a.nutzung], gestaltung: a.gestaltung,
      oberflaeche: o?.oberflaeche, aussehen: o?.aussehen, flaeche_bereich: a.flaeche,
      plz: a.plz, ort: a.ort.trim(), email: a.email.trim(), telefon: a.telefon, name: a.name, info: a.info,
      website: a.website, leistung: a.leistung,
    };
    const fd = new FormData();
    fd.append("daten", JSON.stringify(daten));
    fotos.forEach((f, n) => fd.append("fotos", f.blob, `boden-${n + 1}.jpg`));
    try {
      const r = await fetch(API, { method: "POST", body: fd });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(r.status === 422 ? T.fehler_eingabe : T.fehler);
      setStatus(j.testmodus ? "test" : "ok");
    } catch (e) {
      setStatus("");
      setFehler(e.message === T.fehler_eingabe ? T.fehler_eingabe : T.fehler);
    }
  };

  const zeilen = [
    ...(a.leistung ? [{ n: null, label: "Interesse an", wert: LEISTUNGEN[a.leistung] }] : []),
    ...STRECKE.map((s, n) => (s.art === "wahl" ? { n, label: s.frage, wert: option(s.id, a[s.id])?.text || "" } : null)).filter(Boolean),
    { n: nr("standort"), label: "Standort", wert: `${a.plz} ${a.ort}`.trim() },
    { n: nr("kontakt"), label: "Kontakt", wert: [a.email, a.telefon, a.name].filter(Boolean).join(" · ") },
    { n: nr("kontakt"), label: "Fotos", wert: fotos.length ? `${fotos.length}` : "keine" },
    ...(a.info ? [{ n: nr("kontakt"), label: "Ergänzung", wert: a.info }] : []),
  ];

  if (status === "ok" || status === "test") {
    return (
      <main className="min-h-screen bg-stein-100">
        <Navbar schlicht />
        <div className="max-w-xl mx-auto px-4 pt-28 pb-20" data-testid="offerte-erfolg">
          <div className="aufpoppen bg-white rounded-xl border border-stein-200 p-8 shadow-sm">
            <div className="h-14 w-14 rounded-full grid place-items-center bg-chemara-violet text-white mb-6"><Check size={28} /></div>
            <h1 className="font-heading text-3xl text-stein-900">{T.erfolg_titel}</h1>
            <p className="mt-4 text-stein-700 leading-relaxed">{T.erfolg_text}</p>
            {status === "test" && <p className="mt-4 text-amber-700" data-testid="offerte-testmodus">{T.erfolg_test}</p>}
            <Link to="/" className="mt-8 inline-flex items-center gap-2 text-stein-700 hover:text-stein-900">
              <ArrowLeft size={16} /> Zur Startseite
            </Link>
          </div>
        </div>
      </main>
    );
  }

  const istZusammenfassung = i === ZUSAMMENFASSUNG;
  const spalten = schritt?.optionen?.length === 4 ? "grid-cols-2 lg:grid-cols-4" : "grid-cols-2 sm:grid-cols-3";

  return (
    <main className="min-h-screen bg-stein-100" data-testid="page-offerte">
      <Navbar schlicht />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-20 sm:pt-24 pb-16 grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-5 lg:gap-8 items-start">
        {/* Erklärung (wie beim Vorbild links; auf dem Handy nur bei der ersten Frage) */}
        <aside className={`${i === 0 ? "block" : "hidden lg:block"} bg-stein-900 text-white rounded-xl p-4 lg:p-6`}>
          <h2 className="font-heading text-lg lg:text-2xl leading-tight">{T.seite_intro_titel}</h2>
          <p className="mt-1.5 lg:mt-3 text-sm lg:text-[15px] text-white/75 leading-relaxed">{T.seite_intro}</p>
        </aside>

        <section className="bg-white rounded-xl border border-stein-200 shadow-sm p-5 sm:p-8 min-h-[60vh]">
          <div className="flex items-center justify-between h-7">
            {i > 0 || ausZusammenfassung ? (
              <button type="button" onClick={zurueck} data-testid="offerte-zurueck"
                className="inline-flex items-center gap-1 text-stein-700 hover:text-stein-900 underline underline-offset-4 text-sm">
                <ChevronLeft size={16} /> {T.zurueck}
              </button>
            ) : <span />}
            <span className="text-xs text-stein-500" data-testid="offerte-fortschritt">{i + 1} / {GESAMT}</span>
          </div>
          <div className="mt-3 h-1.5 rounded-full bg-stein-200" aria-hidden="true">
            <div className="h-full rounded-full bg-chemara-violet transition-all duration-500" style={{ width: `${((i + 1) / GESAMT) * 100}%` }} />
          </div>

          <div key={`${i}-${ausZusammenfassung}`} className="aufpoppen">
            <h1 className="mt-6 font-heading text-2xl sm:text-3xl text-stein-900 leading-tight">
              {istZusammenfassung ? T.zusammenfassung : schritt.frage}
            </h1>
            {schritt?.unter && <p className="mt-2 text-stein-500">{schritt.unter}</p>}

            <div className="mt-6">
              {schritt?.art === "wahl" && (
                <div className={`grid ${spalten} gap-3 sm:gap-4`}>
                  {schritt.optionen.map((o, nr) => (
                    <Karte key={o.wert} o={o} nr={nr} aktiv={a[schritt.id] === o.wert}
                      onClick={() => waehlen(schritt.id, o.wert)} testid={`wahl-${schritt.id}-${o.wert}`} />
                  ))}
                </div>
              )}

              {schritt?.art === "standort" && (
                <div className="max-w-lg">
                  <div className="grid grid-cols-3 gap-3">
                    <Feld label={T.plz}>
                      <input inputMode="numeric" maxLength={4} autoComplete="postal-code" data-testid="eingabe-plz" autoFocus
                        value={a.plz} onChange={(e) => setze({ plz: e.target.value.replace(/\D/g, "") })} className={EINGABE} placeholder="8000" />
                    </Feld>
                    <div className="col-span-2">
                      <Feld label={T.ort}>
                        <input autoComplete="address-level2" data-testid="eingabe-ort" value={a.ort} maxLength={80}
                          onChange={(e) => setze({ ort: e.target.value })} className={EINGABE} placeholder="Ort" />
                      </Feld>
                    </div>
                  </div>
                  {a.plz && !fertig.standort && <p className="mt-2 text-sm text-stein-500" data-testid="plz-hinweis">{T.plz_hinweis}</p>}
                  <button type="button" onClick={weiter} disabled={!fertig.standort} data-testid="offerte-weiter" className={`${KNOPF} mt-6 w-full sm:w-auto`}>
                    {T.weiter} <ArrowRight size={18} />
                  </button>
                </div>
              )}

              {schritt?.art === "kontakt" && (
                <div className="space-y-4 max-w-lg">
                  <Feld label={T.email}>
                    <input type="email" inputMode="email" autoComplete="email" required data-testid="eingabe-email" autoFocus
                      value={a.email} onChange={(e) => setze({ email: e.target.value })} className={EINGABE} placeholder="name@beispiel.ch" />
                  </Feld>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Feld label={T.telefon}>
                      <input type="tel" autoComplete="tel" data-testid="eingabe-telefon" value={a.telefon} maxLength={40}
                        onChange={(e) => setze({ telefon: e.target.value })} className={EINGABE} />
                    </Feld>
                    <Feld label={T.name}>
                      <input autoComplete="name" data-testid="eingabe-name" value={a.name} maxLength={120}
                        onChange={(e) => setze({ name: e.target.value })} className={EINGABE} />
                    </Feld>
                  </div>
                  <Feld label={T.info}>
                    <textarea rows={3} data-testid="eingabe-info" value={a.info} maxLength={3000}
                      onChange={(e) => setze({ info: e.target.value })} className={EINGABE} placeholder={T.info_ph} />
                  </Feld>
                  {/* Falle für Bots – für Menschen unsichtbar */}
                  <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" value={a.website}
                    onChange={(e) => setze({ website: e.target.value })} className="hidden" name="website" />
                  <div>
                    <span className="block text-sm text-stein-700 mb-1.5 font-medium">{T.fotos}</span>
                    <div className="flex flex-wrap gap-3">
                      {fotos.map((f, n) => (
                        <div key={f.url} className="relative h-20 w-20">
                          <img src={f.url} alt={`Foto ${n + 1}`} className="h-full w-full object-cover rounded-md" />
                          <button type="button" aria-label="Foto entfernen" onClick={() => setFotos((v) => v.filter((x) => x !== f))}
                            className="absolute -top-2 -right-2 h-6 w-6 rounded-full grid place-items-center bg-stein-900 text-white">
                            <X size={13} />
                          </button>
                        </div>
                      ))}
                      {fotos.length < FOTOS_MAX && (
                        <button type="button" onClick={() => dateiwahl.current?.click()} data-testid="fotos-hinzu"
                          className="h-20 w-20 rounded-md grid place-items-center border-2 border-dashed border-stein-300 hover:border-chemara-violet text-stein-500">
                          <span className="flex flex-col items-center gap-1 text-[11px]"><Camera size={20} />{T.fotos_knopf}</span>
                        </button>
                      )}
                    </div>
                    <input ref={dateiwahl} type="file" accept="image/*,.heic,.heif" multiple className="hidden" data-testid="fotos-datei"
                      onChange={(e) => { fotosHinzu(e.target.files); e.target.value = ""; }} />
                    <p className="mt-2 text-xs text-stein-500">{T.fotos_hinweis}</p>
                  </div>
                  <button type="button" onClick={weiter} disabled={!fertig.kontakt} data-testid="offerte-weiter" className={`${KNOPF} w-full sm:w-auto`}>
                    {T.weiter} <ArrowRight size={18} />
                  </button>
                </div>
              )}

              {istZusammenfassung && (
                <div data-testid="offerte-zusammenfassung">
                  <p className="text-stein-500 -mt-2">{T.zusammenfassung_text}</p>
                  <dl className="mt-4 divide-y divide-stein-200 border-y border-stein-200">
                    {zeilen.map((z, n) => (
                      <div key={n} className="flex items-start gap-4 py-3">
                        <div className="flex-1 min-w-0">
                          <dt className="text-xs text-stein-500">{z.label}</dt>
                          <dd className="mt-0.5 text-[15px] text-stein-900 [overflow-wrap:anywhere] whitespace-pre-line">{z.wert}</dd>
                        </div>
                        {z.n !== null && (
                          <button type="button" onClick={() => aendern(z.n)} data-testid={`aendern-${n}`}
                            className="shrink-0 inline-flex items-center gap-1 text-sm text-chemara-violet hover:underline">
                            <Pencil size={13} /> {T.aendern}
                          </button>
                        )}
                      </div>
                    ))}
                  </dl>
                  <p className="mt-5 text-sm text-stein-500 leading-relaxed">{T.wunsch_hinweis}</p>
                  <p className="mt-1 text-sm text-stein-500 leading-relaxed">
                    {T.datenschutz} <Link to="/privacy" className="underline underline-offset-4">{T.datenschutz_link}</Link>
                  </p>
                  <button type="button" onClick={senden} disabled={status === "sendet"} data-testid="offerte-senden" className={`${KNOPF} mt-6 w-full sm:w-auto text-base`}>
                    {status === "sendet" ? T.sendet : T.senden}
                    {status !== "sendet" && <ArrowRight size={18} />}
                  </button>
                </div>
              )}

              {["gestaltung", "oberflaeche_wahl"].includes(schritt?.id) && (
                <p className="mt-5 text-xs text-stein-500 leading-relaxed">{T.wunsch_hinweis}</p>
              )}
              {fehler && <p className="mt-5 text-red-700" role="alert" data-testid="offerte-fehler">{fehler}</p>}
            </div>
          </div>
        </section>
      </div>
    </main>
  );
};
